/** The deep-link builder: any nonresident.tax page, with `?via=<code>`. */
import {
  isBlockedPrefix,
  isLinkTag,
  isUntrackedPath,
  normalizeDestination,
} from "@nt/partner-links";

/** Asks the rule the API applies, so the two lists of site hosts cannot drift. */
const isSiteHost = (hostname: string): boolean =>
  normalizeDestination(`https://${hostname}/`) !== null;

export type DeepLink =
  { ok: true; url: string } | { ok: false; message: string };

export function buildDeepLink(input: string, code: string): DeepLink {
  const raw = input.trim();
  if (!raw)
    return { ok: false, message: "Paste a nonresident.tax page address." };
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return {
      ok: false,
      message:
        "That is not a web address. Paste a full nonresident.tax page address.",
    };
  }
  if (url.protocol !== "https:" || !isSiteHost(url.hostname)) {
    return {
      ok: false,
      message:
        "Only nonresident.tax pages work. Paste an address that starts with https://nonresident.tax.",
    };
  }
  url.hostname = "nonresident.tax";
  url.searchParams.set("via", code);
  return { ok: true, url: url.toString() };
}

/* ── custom links (create-link drawer) ─────────────────────────────────── */

export const LINK_NAME_MAX = 60;
export const LINK_NOTE_MAX = 255;

/** Null when the tag is empty (optional) or valid; otherwise how to fix it. */
export function validateTag(tag: string): string | null {
  if (tag === "" || isLinkTag(tag)) return null;
  return "Use 1 to 32 lowercase letters, numbers and dashes, starting and ending with a letter or number. Or leave it empty and we make one from the name.";
}

/** Printable ASCII only: the API's `PRINTABLE` rule for names and notes. */
const PRINTABLE = /^[\x20-\x7E]*$/;

export function validateLinkName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed)
    return "Give the link a name, for example YouTube October video.";
  if (trimmed.length > LINK_NAME_MAX)
    return `Shorten the name to ${LINK_NAME_MAX} characters or fewer.`;
  if (!PRINTABLE.test(trimmed))
    return "Use English letters, numbers and punctuation only, for example YouTube October video.";
  return null;
}

export function validateLinkNote(note: string): string | null {
  const trimmed = note.trim();
  if (trimmed.length > LINK_NOTE_MAX)
    return `Shorten the note to ${LINK_NOTE_MAX} characters or fewer.`;
  if (!PRINTABLE.test(trimmed))
    return "Use English letters, numbers and punctuation only in the note.";
  return null;
}

export type Destination =
  { ok: true; path: string } | { ok: false; message: string };

/**
 * A typed destination ("/pricing/" or a nonresident.tax address) as the path
 * and query the API stores. Checked with `buildDeepLink`'s rules; the API
 * sanitises it again and puts it in the site's trailing-slash form.
 */
export function resolveDestination(input: string): Destination {
  const raw = input.trim();
  if (raw.startsWith("//")) {
    return {
      ok: false,
      message:
        "Only nonresident.tax pages work. Type a path such as /pricing/ or paste a nonresident.tax address.",
    };
  }
  const built = buildDeepLink(
    raw.startsWith("/") ? `https://nonresident.tax${raw}` : raw,
    "x",
  );
  if (!built.ok) {
    return {
      ok: false,
      message: raw
        ? built.message
        : "Type a path such as /pricing/ or paste a nonresident.tax page address.",
    };
  }
  const url = new URL(built.url);
  url.searchParams.delete("via");
  url.searchParams.delete("l");
  if (isBlockedPrefix(url.pathname) || isUntrackedPath(url.pathname)) {
    return {
      ok: false,
      message:
        "That address cannot be a destination. Pick a content page on nonresident.tax, such as /pricing/.",
    };
  }
  return { ok: true, path: `${url.pathname}${url.search}` };
}
